import type { Dict } from '../types';

/**
 * A utility for merging objects without mutating. Especially handy in a [].reduce
 * situation. Uses Object.assign with an empty Object `{}` as the target, so that
 * we are producing a copy.
 *
 * @param {...Object} args - All of the Objects you'd like to merge. As many as
 *     you want!
 * @return {Object}
 *
 * @example
 * const x = { name: 'Henry Miller', dob: 'December 26, 1891' };
 * const y = { bibliography: ['Tropic of Cancer', 'Tropic of Capricorn'] };
 * const z = { occupation: 'writer'};
 * const merged = merge(x, y, z);
 * console.log(merged)
 * // {
 * //   name: 'Henry Miller',
 * //   dob: 'December 26, 1891',
 * //   bibliography: ['Tropic of Cancer', 'Tropic of Capricorn'],
 * //   occupation: 'writer'
 * // }
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
 */
export default function merge(...args: Object[]): Object {
  return Object.assign({}, ...args);
}

/**
 * A merge utility to use with Dict<T>
 *
 * Because the signature of Object.assign is an Object, sometimes you want
 * more type assurance that the merged result is still of the target type.
 *
 * @param {Dict<T>} target The starting object for the merge.
 * @param {Dict<T>[]} sources The objects to merge into target, the last specified will take precedence over the first.
 * @return {Dict<T>} The merged dictionary.
 *
 * @example
 * const spanish : Dict<string> = { food: "comida", dog: "perrocito" };
 * const english : Dict<string> = { color: "colour", dog: "mutt" };
 * const result = mergeDict(spanish, english);
 * console.log(result);  // { food: "comida", color: "colour", dog: "mutt" }
 */

export function mergeDicts<T>(target: Dict<T>, ...sources: Dict<T>[]): Dict<T> {
  //Shallow copy
  let result = Object.assign({}, target);

  if (sources.length === 0) {
    return result;
  }

  sources.forEach(dict => {
    result = mergeDict(result, dict);
  });

  return result;
}

/** @ignore **/
function mergeDict<T>(x: Dict<T>, y: Dict<T>): Dict<T> {
  // Do another shallow copy.
  const target = Object.assign({}, x);

  // This uses the fact that for..in will result in prop being a 'string',
  // and access to each dict will yield T.
  for (const prop in y) {
    target[prop] = y[prop];
  }

  // Return copied, merged object.
  return target;
}

/**
 * A merge utility to use with a reduce function.
 *
 * Because the signature of a reduce callback function is:
 *
 * `callback: accumulator currentValue currentIndex array -> any`
 *
 * merge will greedily accept all of the extra arguments and produce a result
 * that you will not be happy with. So mergeReduce wraps {@link merge} and only takes
 * two arguments (accumulator and the current value).
 *
 * @param {Object} x
 * @param {Object} y
 * @return {Object}
 *
 * @example
 * const importantData = [{a: 1}, {b: 2}, {c: 3}].reduce(mergeReduce);
 * console.log(importantData); // {a: 1, b: 2, c: 3}
 *
 */
export function mergeReduce(x: Object, y: Object): Object {
  return merge(x, y);
}
